import { useParams, NavLink } from "react-router-dom";
import axios from "axios";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import { useEffect, useState, useContext } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Facebook, Mail, ThumbsUp } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import AlertModal from "../components/AlertModal";

function SinglePost() {
  const { user } = useContext(AuthContext);
  const { slug } = useParams();
  const token = user?.token;
  const axiosConfig = token
    ? { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
    : { withCredentials: true };

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const [post, setPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [popularPosts, setPopularPosts] = useState([]);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", type: "info" });

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/blogs/slug/${slug}`, {
          credentials: "include", // send cookies/token
        });
        if (!res.ok) throw new Error("Failed to fetch blog post");
        const data = await res.json();
        setPost(data);

        // Initialize likes count
        setLikes(data.likes || 0);

        // Initialize liked state based on current user
        if (user) {
          setLiked(data.likedBy?.includes(user._id) || false);
        }

        // Sidebar posts
        const [recentRes, relatedRes, popularRes] = await Promise.all([
          fetch(`${BASE_URL}/api/blogs?limit=5&sort=recent`),
          fetch(`${BASE_URL}/api/blogs?limit=3&related=${data.category}`),
          fetch(`${BASE_URL}/api/blogs?limit=5&sort=popular`),
        ]);

        const [recentData, relatedData, popularData] = await Promise.all([
          recentRes.json(),
          relatedRes.json(),
          popularRes.json(),
        ]);

        setRecentPosts(recentData);
        setRelatedPosts(relatedData);
        setPopularPosts(popularData);
      } catch (err) {
        console.error(err);
      }
    };

    fetchPost();
  }, [slug, BASE_URL, user?._id, user]);

  const handleLike = async () => {
    if (!user) {
      setAlert({
        open: true,
        message: "Please login or register to like posts.",
        type: "error",
      });
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}/api/blogs/${post._id}/like`, {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to toggle like");

      const data = await res.json();
      setLikes(data.likes);
      setLiked(data.likedBy?.includes(user._id) || false);
    } catch (err) {
      console.error(err);
      setAlert({
        open: true,
        message: "Something went wrong while liking. Try again.",
        type: "error",
      });
    }
  };

  // ✅ Fetch comments once post is loaded
  useEffect(() => {
    if (!post?._id) return; // Wait until post is loaded

    const fetchComments = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/comments/${post._id}`);
        setComments(res.data);
      } catch (error) {
        console.error("Failed to fetch comments:", error);
      }
    };

    fetchComments();
  }, [post?._id, BASE_URL]);

  useEffect(() => {
    if (!alert.open) return;
    const t = setTimeout(() => {
      setAlert({ open: false, message: "", type: "info" });
    }, 3500);
    return () => clearTimeout(t);
  }, [alert.open]);

  // ✅ Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!text.trim()) {
      setAlert({
        open: true,
        message: "Please write a comment.",
        type: "info",
      });
      return;
    }

    if (!post?._id) {
      setAlert({
        open: true,
        message: "Post not loaded yet.",
        type: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        postId: post._id,
        name: user ? user.name : name || "Guest",
        text,
      };

      const res = await axios.post(`${BASE_URL}/api/comments`, payload, axiosConfig);

      if (user) {
        // show immediately for logged-in users
        setComments((prev) => [res.data, ...prev]);
        setAlert({
          open: true,
          message: "Comment posted.",
          type: "success",
        });
      } else {
        // guest — pending approval
        setAlert({
          open: true,
          message: "Your comment has been submitted for approval.",
          type: "success",
        });
      }

      setName("");
      setText("");
    } catch (error) {
      console.error("Failed to post comment:", error);
      setAlert({
        open: true,
        message: "Failed to post comment. Please try again later.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!post) {
    return (
      <div className="text-center mt-20">
        <p>Loading post...</p>
      </div>
    );
  }

  return (
    <>
      {/* Breadcrumbs */}
      <div className="px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs current={post.title} />
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 md:px-6 mt-10 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-4 gap-10" data-aos="fade-up">
          <div className="md:col-span-3">
            <h1 className="text-3xl font-bold text-blue-700 mb-2">{post.title}</h1>
            <p className="text-sm text-gray-500 italic mb-4">
              By {post.author} • Posted on {new Date(post.createdAt).toLocaleDateString()} • Category - {post.category} • Comments({comments.length}) • Likes({likes})
            </p>
            {post.image && (
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-64 object-cover rounded mb-6"
              />
            )}
            <div className="text-gray-700 leading-relaxed whitespace-pre-line mb-6">
              {post.content}
            </div>

            {/* Like and Share */}
            <div className="flex items-center gap-6 mb-10">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 text-sm font-semibold ${
                  liked ? "text-blue-600" : "text-gray-600"
                }`}
              >
                <ThumbsUp
                  size={20}
                  className={`${liked ? "fill-blue-600 text-blue-600" : "text-gray-600"}`}
                />
                {likes} Like{likes !== 1 ? "s" : ""}
              </button>

              <div className="flex items-center gap-4">
                <span className="text-blue-700 font-semibold">Share:</span>

                {/* Facebook */}
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Facebook size={20} />
                </a>

                {/* Twitter */}
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-600"
                >
                  <img src="/images/icons/x-icon-black.png" alt="X.com" className="w-5 h-5" />
                </a>

                {/* Email */}
                <a
                  href={`mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(window.location.href)}`}
                  className="text-red-600 hover:text-red-800"
                >
                  <Mail size={20} />
                </a>
              </div>
            </div>

            {/* Comment Box */}
            <div className="bg-white p-6 rounded shadow mb-10">
              <h3 className="text-lg font-semibold text-blue-700 mb-4">
                Leave a Comment
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!user && (
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-blue-500 focus:outline-none"
                  />
                )}
                <div className="w-full">
                  <textarea
                    rows={4}
                    placeholder="Your Comment (min 5 and max 300 characters)"
                    value={text}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 300) setText(value); // prevent exceeding max length
                    }}
                    className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-blue-500 focus:outline-none"
                    minLength={5}
                    maxLength={300}
                  />
                  <div className="text-sm text-gray-500 mt-1 text-right">
                    {text.length}/300
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={
                    loading || text.trim().length < 5 || text.trim().length > 300
                  }
                  className={`px-6 py-2 rounded transition ${
                    loading || text.trim().length < 5 || text.trim().length > 300
                      ? "bg-blue-400 text-white cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {loading ? "Posting..." : "Submit"}
                </button>
                {!user && (
                  <p className="text-xs text-gray-500">
                    Comments from guests will be shown after admin approval.
                  </p>
                )}
              </form>

              {/* Comments list */}
              <div className="mt-8">
                <h4 className="text-md font-semibold text-gray-700 mb-3">
                  Comments ({comments.length})
                </h4>

                <div className="space-y-4">
                  {comments.length === 0 && (
                    <p className="text-gray-500">No comments yet. Be the first!</p>
                  )}

                  {comments.map((c) => (
                    <div
                      key={c._id}
                      className="border border-gray-200 rounded p-4 bg-gray-50"
                    >
                      <p className="font-medium text-gray-800">{c.name}</p>
                      <p className="text-gray-600 text-sm mt-1">{c.text}</p>
                      <p className="text-gray-400 text-xs mt-2">
                        {new Date(c.createdAt).toLocaleString()}
                      </p>

                      {/* ✅ If Admin replied (array-based) */}
                      {c.reply && c.reply.length > 0 && (
                        <div className="mt-3 ml-4 border-l-4 border-blue-300 pl-3 bg-blue-50 rounded space-y-2">
                          {c.reply.map((r, i) => (
                            <div key={i}>
                              <p className="text-blue-800 font-medium">Admin</p>
                              <p className="text-blue-700 text-sm mt-1">{r.text}</p>
                              <p className="text-blue-400 text-xs mt-1">
                                {new Date(r.createdAt).toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex justify-between text-sm text-blue-600 font-medium mb-10">
              {recentPosts.length > 0 && (
                <NavLink
                  to={`/blog/${recentPosts[0].slug}`}
                  className="hover:underline"
                >
                  ← Previous Post
                </NavLink>
              )}
              {recentPosts.length > 1 && (
                <NavLink
                  to={`/blog/${recentPosts[1].slug}`}
                  className="hover:underline"
                >
                  Next Post →
                </NavLink>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1 space-y-8">
            {[{ title: "Recent Posts", items: recentPosts },
              { title: "Related Posts", items: relatedPosts },
              { title: "Popular Posts", items: popularPosts }
            ].map((group, index) => (
              <div key={index} className="bg-blue-50 p-4 rounded shadow">
                <h3 className="text-blue-700 font-semibold mb-3">{group.title}</h3>
                <ul className="text-sm text-gray-600 space-y-2">
                  {group.items.map((postItem, i) => (
                    <li key={i}>
                      <NavLink to={`/blog/${postItem.slug}`} className="hover:underline text-blue-600">
                        {postItem.title}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <AlertModal
        isOpen={alert.open}
        onClose={() => setAlert({ open: false, message: "", type: "info" })}
        message={alert.message}
        type={alert.type}
      />

      {/* Footer */}
      <div data-aos="fade-up">
        <Footer />
      </div>
    </>
  );
}

export default SinglePost;
